import React, { useEffect, useRef, useState } from 'react';
import { X, Plus, Minus, MessageCircle, ShoppingBag, Lock } from 'lucide-react';
import { SIZES, CURRENCY_SYMBOL, waProduct } from '../config/pricing';

export default function ProductDetailDrawer({ product, onClose, onAdd }) {
  const [renderedProduct, setRenderedProduct] = useState(product);
  const [visible, setVisible] = useState(false);
  const [qty, setQty] = useState(1);
  const [imgOk, setImgOk] = useState(true);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const size = SIZES[0];
  const price = size.price * qty;

  useEffect(() => {
    let frame;
    let timer;
    if (product) {
      previousFocusRef.current = document.activeElement;
      setRenderedProduct(product);
      setQty(1);
      setImgOk(true);
      frame = requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      timer = window.setTimeout(() => setRenderedProduct(null), 380);
    }
    return () => {
      if (frame) cancelAnimationFrame(frame);
      if (timer) window.clearTimeout(timer);
    };
  }, [product]);

  useEffect(() => {
    if (!renderedProduct) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      requestAnimationFrame(() => {
        if (!document.querySelector('.drawer.open, .product-drawer.open')) {
          document.body.style.overflow = '';
        }
      });
      previousFocusRef.current?.focus?.();
    };
  }, [renderedProduct, onClose]);

  useEffect(() => {
    if (!visible) return undefined;
    const timer = window.setTimeout(() => closeButtonRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!renderedProduct) return null;

  return (
    <>
      <div className={`product-backdrop ${visible ? 'open' : ''}`} onClick={onClose} />
      <aside className={`product-drawer ${visible ? 'open' : ''}`} role="dialog" aria-modal="true" aria-hidden={!visible} aria-label={`${renderedProduct.name} details`}>
        <header className="product-drawer-head">
          <div>
            <span className="card-cat">{renderedProduct.category}</span>
            <h3>{renderedProduct.name}</h3>
          </div>
          <button ref={closeButtonRef} type="button" className="icon-btn" onClick={onClose} aria-label="Close product details"><X size={18} /></button>
        </header>

        <div className="product-drawer-body">
          <div className="product-drawer-media">
            {imgOk ? (
              <img src={renderedProduct.image} alt={renderedProduct.name} width="720" height="792" loading="eager" decoding="async" onError={() => setImgOk(false)} />
            ) : (
              <div className="mini-bottle" aria-hidden="true" />
            )}
          </div>

          <p className="product-drawer-desc">{renderedProduct.description}</p>
          <div className="product-facts">
            <p><strong>Profile</strong>{renderedProduct.profile}</p>
            <p><strong>Best for</strong>{renderedProduct.bestFor}</p>
            <p><strong>Notes</strong>{renderedProduct.notes.slice(0, 7).join(', ')}</p>
          </div>

          <div className="product-offer drawer-offer" aria-label={`${size.label}, ${CURRENCY_SYMBOL}${size.price}`}>
            <span className="offer-size"><strong>{size.ml}ml</strong><small>Oud oil</small></span>
            <strong className="offer-price">{CURRENCY_SYMBOL}{size.price}</strong>
          </div>

          <div className="qty-row">
            <div className="qty" aria-label="Quantity">
              <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity"><Minus size={14} /></button>
              <span>{qty}</span>
              <button type="button" onClick={() => setQty(qty + 1)} aria-label="Increase quantity"><Plus size={14} /></button>
            </div>
            <span className="price-tag">{CURRENCY_SYMBOL}{price}</span>
          </div>
        </div>

        <footer className="product-drawer-foot">
          <button
            type="button"
            className="btn btn-gold btn-block"
            onClick={() => {
              onAdd(renderedProduct, size, qty);
              onClose();
            }}
          >
            <ShoppingBag size={15} /> Add {qty > 1 ? `${qty} to Bag` : 'to Bag'}
          </button>
          <a className="btn btn-ghost btn-block" href={waProduct(renderedProduct.name, size.label)} target="_blank" rel="noopener noreferrer">
            <MessageCircle size={15} /> Order on WhatsApp
          </a>
          <p className="drawer-foot-note"><Lock size={11} /> Secure checkout · Free UK delivery</p>
        </footer>
      </aside>
    </>
  );
}
